/* eslint-disable no-console, no-underscore-dangle */
import React, { useRef, useEffect, createContext, useState } from 'react';
import { Centrifuge, Subscription } from 'centrifuge';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import config from 'config';
import { SocketIoEventMsg, SocketIoEntityMsg } from '../Common/types';
import {
  publishSocketIoEventMsg,
  publishSocketIoEntityMsg,
} from '../socketio/topics';
import { getBeepWebSocketUrl } from './apiClient/url';

export interface BeepConnectionState {
  state: string;
  connected: boolean;
  transport: string;
}

export const getBeepState = (): BeepConnectionState => {
  if (typeof window !== 'undefined' && (window as any).beepCentrifuge) {
    const centrifuge = (window as any).beepCentrifuge;
    return {
      state: centrifuge.state || 'unknown',
      connected: centrifuge.state === 'connected',
      transport: centrifuge.transport?.name || 'none',
    };
  }
  return { state: 'unknown', connected: false, transport: 'none' };
};

const BeepContext = createContext<Subscription | undefined>(undefined);
export { BeepContext };

interface BeepProviderProps {
  children: React.ReactNode;
}

export const BeepProvider: React.FC<BeepProviderProps> = ({ children }) => {
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const [subscription, setSubscription] = useState<Subscription | undefined>(
    undefined
  );
  const isConnected = useRef(false);
  const connectionStatus = useRef('disconnected');

  const handleBeepPublication = (ctx: any) => {
    if (!config.IN_PRODUCTION) {
      console.log('📨 BEEP: Received publication:', ctx.data);
    }

    if (ctx.data._class === 'event') {
      if (!config.IN_PRODUCTION) {
        console.log('🎯 BEEP: Publishing event message:', ctx.data);
      }
      publishSocketIoEventMsg(ctx.data as SocketIoEventMsg);
    } else if (ctx.data._class === 'entity') {
      if (!config.IN_PRODUCTION) {
        console.log('🎯 BEEP: Publishing entity message:', ctx.data);
      }
      publishSocketIoEntityMsg(ctx.data as SocketIoEntityMsg);
    } else if (!config.IN_PRODUCTION) {
      console.log('❓ BEEP: Unknown message type:', ctx.data);
    }
  };

  const connectToBeep = async () => {
    if (!account?.userToken || !account?.id) {
      return;
    }

    if (
      centrifugeRef.current?.state === 'connecting' ||
      centrifugeRef.current?.state === 'connected'
    ) {
      if (!config.IN_PRODUCTION) {
        console.log('BEEP: Already connected', centrifugeRef.current?.state);
      }
      return;
    }

    try {
      if (!config.IN_PRODUCTION) {
        console.log('🔧 BEEP: Setting up connection with token:', {
          hasToken: !!account.userToken,
          tokenLength: account.userToken?.length || 0,
          userId: account.id,
          tokenStart: `${account.userToken?.substring(0, 8)}...`,
        });
      }

      const wsUrl = getBeepWebSocketUrl();

      centrifugeRef.current = new Centrifuge(wsUrl, {
        debug: !config.IN_PRODUCTION,
        maxReconnectDelay: 20000,
        minReconnectDelay: 1000,
        timeout: 10000,
        headers: {
          Authorization: `Token ${account.userToken}`,
          'pos-account-id': account.id.toString(),
          'User-Agent': 'Shore-Frontend/1.0',
          'X-Api-Version': '13',
        },
      });

      // Listen for subscription events (happen after connected)
      centrifugeRef.current.on('subscribed', (ctx) => {
        if (!config.IN_PRODUCTION) {
          console.log('🎯 BEEP: Auto-subscribed to channel:', ctx.channel);
        }

        // Set up handler for this subscription
        const sub = centrifugeRef.current?.getSubscription(ctx.channel);
        if (sub) {
          sub.on('publication', handleBeepPublication);
          if (!config.IN_PRODUCTION) {
            console.log(`✅ BEEP: Handler set up for channel: ${ctx.channel}`);
          }

          // Set the first subscription as active for context provider
          if (!subscription) {
            setSubscription(sub);
            if (!config.IN_PRODUCTION) {
              console.log(
                `🎯 BEEP: Set ${ctx.channel} as active subscription for context`
              );
            }
          }
        }
      });

      centrifugeRef.current.on('connected', (ctx) => {
        isConnected.current = true;
        connectionStatus.current = 'connected';
        if (!config.IN_PRODUCTION) {
          console.log('✅ BEEP: Connected to Centrifugo');
          console.log('🎯 Connection details:', {
            state: centrifugeRef.current?.state,
            url: wsUrl,
            user: ctx.data?.user,
            autoSubscribedChannels: ctx.data?.channels,
          });
          console.log('🔗 Client state:', centrifugeRef.current?.state);
        }

        if (ctx.data?.channels && Array.isArray(ctx.data.channels)) {
          if (!config.IN_PRODUCTION) {
            console.log(
              `🎯 BEEP: Setting up handlers for ${ctx.data.channels.length} auto-subscribed channels...`
            );
          }
          let activeSubscription: any = null;

          ctx.data.channels.forEach((channel: string) => {
            try {
              const sub = centrifugeRef.current?.getSubscription(channel);
              if (sub) {
                sub.on('publication', handleBeepPublication);
                if (!config.IN_PRODUCTION) {
                  console.log(
                    `✅ BEEP: Set up handler for auto-subscribed channel: ${channel}`
                  );
                }

                if (!activeSubscription) {
                  activeSubscription = sub;
                }
              } else if (!config.IN_PRODUCTION) {
                console.log(
                  `⚠️ BEEP: No subscription found for auto-subscribed channel: ${channel}`
                );
              }
            } catch (error) {
              // Suppress error logging in production
            }
          });

          if (activeSubscription) {
            setSubscription(activeSubscription);
            if (!config.IN_PRODUCTION) {
              console.log(
                '🎯 BEEP: Active auto-subscription set for context provider'
              );
            }
          }
        } else if (!config.IN_PRODUCTION) {
          console.log(
            '📋 No channel data in ctx.data - waiting for individual subscription events...'
          );
        }
      });

      centrifugeRef.current.on('disconnected', (ctx) => {
        isConnected.current = false;
        connectionStatus.current = 'disconnected';
        if (!config.IN_PRODUCTION) {
          console.log('🔌 BEEP: Disconnected from Centrifugo');
          console.log('📊 Disconnect reason:', ctx.reason, ctx.code);
          console.log('🔗 Client state:', centrifugeRef.current?.state);
        }
      });

      centrifugeRef.current.on('error', (ctx) => {
        connectionStatus.current = 'error';

        if (ctx.type === 'transport') {
          if (!config.IN_PRODUCTION) {
            console.warn('BEEP: Transport error - server connection refused');
          }
          if (centrifugeRef.current) {
            centrifugeRef.current.disconnect();
          }
        } else if (ctx.error?.toString().includes('3502')) {
          centrifugeRef.current?.disconnect();
        }
      });

      if (typeof window !== 'undefined') {
        (window as any).beepCentrifuge = centrifugeRef.current;
      }

      centrifugeRef.current.connect();
    } catch (error) {
      // Suppress connection error logging in production
      connectionStatus.current = 'error';
    }
  };

  const disconnectFromBeep = () => {
    if (centrifugeRef.current) {
      centrifugeRef.current.disconnect();
      centrifugeRef.current = null;
      isConnected.current = false;
      connectionStatus.current = 'disconnected';
      setSubscription(undefined);
      if (!config.IN_PRODUCTION) {
        console.log(
          '🔌 BEEP: Disconnected and cleaned up - auto-subscriptions will be cleaned up automatically'
        );
      }
    }
  };

  useEffect(() => {
    if (account?.userToken && account?.id) {
      connectToBeep();
    }

    return () => {
      disconnectFromBeep();
    };
  }, [account?.userToken, account?.id]);

  return (
    <BeepContext.Provider value={subscription}>{children}</BeepContext.Provider>
  );
};

export default BeepProvider;
